/**
 * Supabaseクライアント設定
 * Supabaseへの接続を管理します
 */

import { createClient } from '@supabase/supabase-js';
import { getPublicRuntimeConfig } from '../../shared/config/env';
import { createTimeoutFetch, SUPABASE_REQUEST_TIMEOUT_MS } from './timeoutFetch';

/**
 * キャッシュ済みSupabaseクライアント
 */
let cachedSupabaseClient = null;

/**
 * 呼び出した時点のブラウザのfetchで通信する
 * @param {RequestInfo | URL} input - 通信先
 * @param {RequestInit} [init] - 通信の設定
 * @returns {Promise<Response>} 応答
 */
const fetchWithBrowser = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => fetch(input, init);

/**
 * Supabaseクライアントを生成する
 * 来場者アプリはSupabase Authを使わない（独自のセッショントークン）ため、Authのセッションを保存しない。
 * localStorageを渡すと、Cookieを全ブロックした端末では参照時のSecurityErrorでクライアントを作れず、全RPCが止まる。
 * @returns {import('@supabase/supabase-js').SupabaseClient} Supabaseクライアント
 */
const createSupabaseClient = () => {
  /** ランタイム設定 */
  const runtimeConfig = getPublicRuntimeConfig();

  /** Supabaseクライアント */
  const supabaseClient = createClient(runtimeConfig.supabase.url, runtimeConfig.supabase.anonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
    global: {
      fetch: createTimeoutFetch(fetchWithBrowser, SUPABASE_REQUEST_TIMEOUT_MS),
    },
  });

  return supabaseClient;
};

/**
 * Supabaseクライアントを取得する
 * @returns {import('@supabase/supabase-js').SupabaseClient} Supabaseクライアント
 */
export const getSupabaseClient = () => {
  if (!cachedSupabaseClient) {
    cachedSupabaseClient = createSupabaseClient();
  }

  return cachedSupabaseClient;
};

/**
 * キャッシュ済みSupabaseクライアントを初期化する
 * @returns {void}
 */
export const resetSupabaseClient = () => {
  cachedSupabaseClient = null;
};
